import { isObject } from 'lodash';
import { BASE_MEETING_PLAN_TYPES, planTypes } from '@/common/constants';
import { $context } from '@/global-install';
import { persistCartConstant } from '@/views/config/shopping-cart/persist/persist-cart';
import { pageConfig } from '@/common/service/page-config';
import store from '@/store/index';
import { ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';

const isSelectedPlanDependOnTargetPlan = (type, draftSelectedPlans) => {
  return draftSelectedPlans
    .filter((item) => item?.type !== type)
    .some((item) => item?.dependBasePlanTypeList?.includes(type));
};

const isContainsStandalone = (type, draftSelectedPlans) => {
  return draftSelectedPlans
    .filter((item) => item?.type !== type)
    .some((item) => item?.dependBasePlanTypeList?.length === 0);
};
//Sort by draftPlanFilterKeys
const getDraftPlans = (planMapFlat, draftPlanFilterKeys) => {
  const resultMap = new Map();
  const additionalPlanType = [planTypes.ADDITIONAL_HOST, planTypes.ADDITIONAL_CO_EDITOR];

  for (const item of planMapFlat) {
    const planType = planTypes[item.type];
    const basePlanType = planTypes[item.baseType];
    const isAdditionalType = additionalPlanType.includes(item.type);

    const shouldAddItem =
      draftPlanFilterKeys.includes(planType) &&
      (!isAdditionalType || draftPlanFilterKeys.includes(basePlanType));

    if (shouldAddItem && !resultMap.has(planType)) {
      resultMap.set(planType, item);
    }
  }
  return draftPlanFilterKeys
    .map((type) => resultMap.get(type))
    .filter(Boolean);
};

// product can remove common methods
export const isProductCanRemove = (currentPlan) => {
  const { planMapFlat, draftPlanFilterKeys } = store.getters;
  // Special case, zoom event services depend on webinar single use.
  // but this plan can't remove by itself, When you remove webinar single use, this plan will be removed automatically
  const draftPlanFilterKeysWithoutEventServices = draftPlanFilterKeys.filter(
    (planType) => planType !== planTypes.zoom_event_services,
  );
  const draftSelectedPlans = getDraftPlans(planMapFlat, draftPlanFilterKeysWithoutEventServices);

  if (!isObject(currentPlan) || !Array.isArray(draftSelectedPlans)) {
    return false;
  }

  if (draftSelectedPlans.length < 2) {
    return false;
  }

  // Special cases, for zoom_pro_plus, zoom phone & zoom pro plus use the same config card
  if ([planTypes.ZONE_PRO_PLUS, planTypes.ZONE_EDU_PLUS, planTypes.ZOOM_ONE_BUNDLE].includes(currentPlan?.type)) {
    if (draftSelectedPlans.some(plan => plan.type === planTypes.ZOOM_PHONE)) {
      return false;
    }
    const leftPlans = draftSelectedPlans.filter(
      (plan) =>
        ![planTypes.ZOOM_PHONE, planTypes.ZOOM_ONE_BUNDLE, planTypes.ZONE_PRO_PLUS, planTypes.ZONE_EDU_PLUS].includes(
          plan.type,
        ),
    );
    if (leftPlans.length < 1) {
      return false;
    }
  }

  const isCurrentPlanDepended = isSelectedPlanDependOnTargetPlan(currentPlan.type, draftSelectedPlans);

  // addon: if other addon depend on this addon, we can't remove
  if (currentPlan?.dependBasePlanTypeList?.length && isCurrentPlanDepended) {
    return false;
  }

  // basePlan: addon depend this product || no other standalone product
  // current base plan can't remove
  const isStandaloneExist = isContainsStandalone(currentPlan.type, draftSelectedPlans);
  if (isCurrentPlanDepended || !isStandaloneExist) {
    return false;
  }

  // standalone or base plan without dependency
  return true;
};

export const isFromShoppingCart = () => {
  const isSupportedFlow = $context.is.buyNow || $context.is.buyTrial || $context.is.basic2pro;

  return (
    persistCartConstant?.enablePersistShoppingBag &&
    $context?.is?.fromPersistentCart &&
    isSupportedFlow &&
    !window.campaignFlags?.disableRemovePlanWithPersistCart
  );
};

export const isProductExcludedInShoppingCart = (selectedPlans) => {
  return isFromShoppingCart() && !selectedPlans.some((plan) => plan?.type === pageConfig?.editConfig?.planType);
};

export const checkBasePlanCanRemove = (currentPlan) => {
  const { planMapFlat, draftPlanFilterKeys } = store.getters;
  // Special case, zoom event services depend on webinar single use.
  // but this plan can't remove by itself, When you remove webinar single use, this plan will be removed automatically
  const draftPlanFilterKeysWithoutEventServices = draftPlanFilterKeys.filter(
    (planType) => planType !== planTypes.zoom_event_services,
  );
  const draftSelectedPlans = getDraftPlans(planMapFlat, draftPlanFilterKeysWithoutEventServices);
  const isCurrentPlanDepended = isSelectedPlanDependOnTargetPlan(currentPlan.type, draftSelectedPlans);
  // basePlan: addon depend this product || no other standalone product
  // current base plan can't remove
  const isStandaloneExist = isContainsStandalone(currentPlan.type, draftSelectedPlans);
  if (isCurrentPlanDepended || !isStandaloneExist) {
    return false;
  }
  return true;
}
export const isMeetingPlanDisabledByHasAddOn = (planType) => {
  const {  pastPlans,getSelectedPlans } = store.getters;
  //todo If you need to use it in other scenarios later, you can remove winBack.
  if (pastPlans.length === 0) return false;
  const index = getSelectedPlans.findIndex(item => item.type === planType);
  if (index <= 0) {
    return false;
  }
  const relatePlans = getSelectedPlans.filter(item => item.type !== planType && item.dependBasePlanTypeList && item.dependBasePlanTypeList.length > 0 && item.dependBasePlanTypeList.includes(planType));
  if (relatePlans.length === 0) {
    return false;
  }
  //zoomRoom cannot be used as basePlan when there is meetingPlan
  const otherSelectedTypes = getSelectedPlans
    .map(plan => plan.type)
    .filter(type => type !== planType && type !== planTypes.ZOOM_ROOMS);
  const hasUnmatchedDependency = relatePlans.some(plan =>
    plan.dependBasePlanTypeList.every(dependency => !otherSelectedTypes.includes(dependency))
  );
  return hasUnmatchedDependency;
};

export const canRemoveMeetingPlanInWinBack = (planType,zoomMeetingCategory='') => {
  const { pastPlans} = store.getters;
  if (isMeetingPlanDisabledByHasAddOn(planType)) {
    return false;
  }
  //Sometimes pro is not recommended, but pro will be automatically added as default.
  //pro can switch biz
  if (Object.values(ZOOM_MEETING_SUB_TYPES).includes(pageConfig.plan)) {
    return false;
  }
  //To avoid selecting duplicate recommended items, add winback and then enter from the shopping cart.
  const meetingDoubleCheck = (isFromShoppingCart() || pastPlans.length > 0) && planTypes.ZOOM_MEETING === planType;
  return window.$context.is.basic2pro && (meetingDoubleCheck || pastPlans.some(item => [planTypes.ZOOM_ROOMS, ...BASE_MEETING_PLAN_TYPES].includes(planTypes[item.semanticType]))) && pageConfig.plan!==zoomMeetingCategory;
};
export const canRemoveAddOnPlanInWinBack = (planType) => {
  const { pastPlans } = store.getters;
  return window.$context.is.basic2pro && pastPlans.some(item => planTypes[item.semanticType] === planType);
};
